(() => {
  'use strict';

  const instanceKey = Symbol.for('paawsy.cat.replica.initialized');

  function initialize() {
    const page = document.body;
    if (!page || !page.matches('[data-pawsy-page]') || page[instanceKey]) return;
    page[instanceKey] = true;

    const all = (selector, root = page) => Array.from(root.querySelectorAll(selector));
    const one = (selector, root = page) => root.querySelector(selector);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => reducedMotion.matches ? 'instant' : 'smooth';
    const positiveId = (value) => {
      if (typeof value !== 'number' && typeof value !== 'string') return null;
      if (typeof value === 'string' && !/^[1-9]\d*$/.test(value)) return null;
      const number = Number(value);
      return Number.isSafeInteger(number) && number > 0 ? number : null;
    };
    const validMoney = (value) => Number.isSafeInteger(value) && value >= 0;
    const setText = (selector, text) => all(selector).forEach((node) => { node.textContent = text; });
    const onFrame = (callback) => {
      let frame = 0;
      return () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          callback();
        });
      };
    };
    const centerInTrack = (track, item, animate = true) => {
      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const left = track.scrollLeft + itemRect.left - trackRect.left - track.clientLeft
        - (track.clientWidth - itemRect.width) / 2;
      track.scrollTo({ left, behavior: animate ? behavior() : 'instant' });
    };

    const galleries = all('.pc-gallery-track').map((track) => {
      const container = track.closest('.pc-gallery') || page;
      const items = Array.from(track.children).filter((child) => child.hasAttribute('data-pc-media-id'));
      const thumbs = all('button[data-pc-thumb]', container);
      const steps = all('[data-pc-gallery-step]', container);
      let active = 0;
      track.style.scrollSnapType = 'x mandatory';
      track.style.overflowX = 'auto';
      items.forEach((item) => { item.style.scrollSnapAlign = 'center'; });
      const thumbId = (thumb) => thumb.getAttribute('value') || thumb.getAttribute('data-pc-thumb');
      const update = (index) => {
        if (!items.length) return;
        active = Math.max(0, Math.min(items.length - 1, index));
        const id = items[active].getAttribute('data-pc-media-id');
        thumbs.forEach((thumb) => {
          const selected = thumbId(thumb) === id;
          thumb.setAttribute('aria-current', selected ? 'true' : 'false');
          thumb.classList.toggle('is-active', selected);
        });
        steps.forEach((button) => {
          const direction = Number(button.getAttribute('data-pc-gallery-step'));
          button.disabled = !items.length || (direction < 0 ? active === 0 : active === items.length - 1);
        });
      };
      const go = (index, animate = true) => {
        if (!items.length) return;
        update(index);
        centerInTrack(track, items[active], animate);
      };
      const nearest = () => {
        if (!items.length || !track.clientWidth) return;
        const rect = track.getBoundingClientRect();
        const center = rect.left + track.clientLeft + track.clientWidth / 2;
        let distance = Infinity;
        let index = 0;
        items.forEach((item, current) => {
          const itemRect = item.getBoundingClientRect();
          const delta = Math.abs(itemRect.left + itemRect.width / 2 - center);
          if (delta < distance) {
            index = current;
            distance = delta;
          }
        });
        update(index);
      };
      thumbs.forEach((thumb) => thumb.addEventListener('click', (event) => {
        event.preventDefault();
        const index = items.findIndex((item) => item.getAttribute('data-pc-media-id') === thumbId(thumb));
        if (index >= 0) go(index);
      }));
      steps.forEach((button) => button.addEventListener('click', (event) => {
        event.preventDefault();
        const step = Number(button.getAttribute('data-pc-gallery-step'));
        if (step === -1 || step === 1) go(active + step);
      }));
      track.addEventListener('scroll', onFrame(nearest), { passive: true });
      track.addEventListener('scrollend', nearest);
      window.addEventListener('resize', onFrame(nearest), { passive: true });
      update(0);
      return { items, go };
    });

    const imagePath = (source) => {
      if (!source) return '';
      try {
        const url = new URL(source, window.location.href);
        return decodeURIComponent(url.pathname).replace(/_\d+x\d*(?=\.[^.]+$)/, '');
      } catch (_) {
        return String(source).split('?')[0];
      }
    };
    const showVariantMedia = (variant, animate = true) => {
      const image = variant && variant.featured_image;
      if (!image || (!image.src && !image.id)) return;
      galleries.forEach((gallery) => {
        const index = gallery.items.findIndex((item) => {
          const img = item.matches('img') ? item : one('img', item);
          if (image.id != null && (String(image.id) === item.getAttribute('data-pc-media-id')
            || (img && [img.id, img.getAttribute('data-media-id'), img.getAttribute('data-pc-media-id')]
              .includes(String(image.id))))) return true;
          if (!img || !image.src) return false;
          return [img.currentSrc, img.getAttribute('src'), img.getAttribute('data-src')]
            .some((source) => source && imagePath(source) === imagePath(image.src));
        });
        if (index >= 0) gallery.go(index, animate);
      });
    };

    const purchase = one('form#pc-purchase');
    const submitButtons = all('[data-pc-submit]');
    let submitting = false;
    let currentVariant = null;
    let payload = {};
    let dataError = '';
    const dataElement = one('script#pc-variants');
    try {
      if (!dataElement || dataElement.type !== 'application/json') throw new Error('Missing product data.');
      payload = JSON.parse(dataElement.textContent);
      if (!payload || typeof payload !== 'object' || !Array.isArray(payload.variants)) {
        throw new Error('Invalid product data.');
      }
    } catch (_) {
      payload = {};
      dataError = 'Product options could not be loaded. Please refresh the page.';
    }
    const variants = Array.isArray(payload.variants) ? payload.variants.filter((variant) =>
      variant && typeof variant.id === 'number' && positiveId(variant.id)
      && Array.isArray(variant.options) && variant.options.every((option) => typeof option === 'string')
      && validMoney(variant.price) && typeof variant.available === 'boolean') : [];
    const variantsById = new Map(variants.map((variant) => [variant.id, variant]));
    let formatter;
    try {
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: payload.currency || 'USD'
      });
    } catch (_) {
      formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
    const money = (cents) => formatter.format(cents / 100);
    let root = '/';
    if (typeof payload.root === 'string' && /^\/(?!\/)/.test(payload.root)) {
      try {
        const rootURL = new URL(payload.root, window.location.origin);
        if (rootURL.origin === window.location.origin) root = rootURL.pathname.replace(/\/?$/, '/');
      } catch (_) {
        root = '/';
      }
    }
    let errorBox = one('#pc-error');
    const showError = (message) => {
      if (!errorBox && purchase) {
        errorBox = document.createElement('p');
        errorBox.id = 'pc-error';
        purchase.appendChild(errorBox);
      }
      if (!errorBox) return;
      errorBox.setAttribute('role', 'alert');
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    };
    const idInput = purchase && one('input[type="hidden"][name="id"]', purchase);
    const quantityInput = purchase && one('input[type="hidden"][name="quantity"]', purchase);
    const optionSelects = purchase ? all('select[data-pc-option]', purchase) : [];
    const packInputs = purchase ? all('input[type="radio"][name="pc-pack"]', purchase) : [];
    const packQuantity = () => {
      const checked = packInputs.find((input) => input.checked);
      return checked && (checked.value === '1' || checked.value === '2') ? Number(checked.value) : null;
    };
    const matchingVariant = () => variants.find((variant) => optionSelects.every((select) => {
      const index = Number(select.getAttribute('data-pc-option'));
      return Number.isInteger(index) && index >= 0 && variant.options[index] === select.value;
    }));
    const hasValidSelection = () => Boolean(purchase && idInput && quantityInput && currentVariant
      && positiveId(currentVariant.id) && currentVariant.available === true
      && packQuantity() && positiveId(idInput.value) === currentVariant.id
      && Number(quantityInput.value) === packQuantity());
    const updateButtons = () => submitButtons.forEach((button) => {
      button.disabled = submitting || !hasValidSelection();
      button.setAttribute('aria-disabled', String(button.disabled));
      button.setAttribute('aria-busy', String(submitting));
    });
    const updateURL = (variant) => {
      try {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', String(variant.id));
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch (_) {
        // Selection must still work when history changes are restricted by the host.
      }
    };
    const setSelects = (variant) => {
      optionSelects.forEach((select) => {
        const index = Number(select.getAttribute('data-pc-option'));
        if (Number.isInteger(index) && index >= 0) select.value = variant.options[index] || '';
      });
    };
    const renderSelection = (variant, { changeURL = true, media = true, animate = true } = {}) => {
      currentVariant = variant || null;
      if (idInput) idInput.value = currentVariant ? String(currentVariant.id) : '';
      if (quantityInput) quantityInput.value = packQuantity() ? String(packQuantity()) : '';
      setText('[data-pc-price]', currentVariant ? money(currentVariant.price) : '—');
      all('[data-pc-compare]').forEach((node) => {
        const compare = currentVariant && validMoney(currentVariant.compare_at_price)
          && currentVariant.compare_at_price > currentVariant.price ? currentVariant.compare_at_price : null;
        node.hidden = compare === null;
        node.textContent = compare === null ? '' : money(compare);
      });
      [1, 2].forEach((quantity) => setText(`[data-pc-pack-price="${quantity}"]`,
        currentVariant ? money(Math.round(currentVariant.price * (quantity === 2 ? 1.6 : 1))) : '—'));
      if (changeURL) updateURL(currentVariant);
      if (media && currentVariant) showVariantMedia(currentVariant, animate);
      updateButtons();
    };
    const selectionError = () => dataError || (!currentVariant
      ? 'This combination is unavailable. Please choose another option.'
      : !currentVariant.available ? 'This variant is sold out. Please choose another option.'
        : !packQuantity() ? 'Please choose a pack of one or two.'
          : 'This selection is invalid. Please refresh the page.');

    if (purchase) {
      if (!packInputs.some((input) => input.checked)) {
        const single = packInputs.find((input) => input.value === '1');
        if (single) single.checked = true;
      }
      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      const initialId = urlVariant !== null ? positiveId(urlVariant) : idInput && positiveId(idInput.value);
      let initialVariant = initialId ? variantsById.get(initialId) : null;
      if (urlVariant === null && !initialVariant) {
        initialVariant = optionSelects.length ? matchingVariant() : variants.find((variant) => variant.available) || variants[0];
      }
      if (initialVariant) {
        setSelects(initialVariant);
        if (optionSelects.length && matchingVariant() !== initialVariant) initialVariant = null;
      }
      renderSelection(initialVariant, { changeURL: urlVariant === null || Boolean(initialVariant), animate: false });
      if (!hasValidSelection()) showError(selectionError());
      else showError('');

      optionSelects.forEach((select) => select.addEventListener('change', () => {
        showError('');
        renderSelection(matchingVariant());
        if (!hasValidSelection()) showError(selectionError());
      }));
      packInputs.forEach((input) => input.addEventListener('change', () => {
        showError('');
        renderSelection(currentVariant, { media: false });
        if (!hasValidSelection()) showError(selectionError());
      }));
      window.addEventListener('popstate', () => {
        const id = positiveId(new URL(window.location.href).searchParams.get('variant'));
        let variant = id ? variantsById.get(id) : null;
        if (variant) {
          setSelects(variant);
          if (optionSelects.length && matchingVariant() !== variant) variant = null;
        }
        renderSelection(variant, { changeURL: false });
        showError(hasValidSelection() ? '' : selectionError());
      });

      const updateCartCount = async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(root + 'cart.js', {
            credentials: 'same-origin', headers: { Accept: 'application/json' },
            cache: 'no-store', signal: controller.signal
          });
          if (!response.ok) return;
          const cart = await response.json();
          if (Number.isSafeInteger(cart.item_count) && cart.item_count >= 0) {
            setText('[data-pc-cart-count]', String(cart.item_count));
          }
        } catch (_) {
          // A cart-count refresh failure must never repeat a successful add request.
        } finally {
          window.clearTimeout(timeout);
        }
      };
      const cartSuccess = (result, quantity) => {
        const item = Array.isArray(result.items) ? result.items[0] : result;
        const title = item && (item.product_title || item.title);
        setText('[data-pc-cart-title]', 'Added to your cart');
        setText('[data-pc-cart-message]', `${quantity} × ${title || 'your selected item'} has been added to your cart.`);
        const dialog = one('#pc-cart-dialog');
        if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
          try { dialog.showModal(); } catch (_) {
            showError('Added to your cart. Open your cart to review your items.');
          }
        } else if (!dialog || typeof dialog.showModal !== 'function') {
          showError('Added to your cart. Open your cart to review your items.');
        }
      };
      purchase.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitting) return;
        showError('');
        if (!hasValidSelection() || (optionSelects.length && matchingVariant() !== currentVariant)) {
          showError(selectionError());
          updateButtons();
          return;
        }
        const variant = currentVariant;
        const quantity = packQuantity();
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 20000);
        submitting = true;
        updateButtons();
        let acknowledged = false;
        try {
          const response = await fetch(root + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: Number(quantity) }] }),
            signal: controller.signal
          });
          acknowledged = response.ok;
          let result = {};
          try { result = await response.json(); } catch (_) { /* HTTP success already confirms the addition. */ }
          if (!response.ok) {
            const description = result && typeof result.description === 'string' ? result.description
              : result && typeof result.message === 'string' ? result.message
                : `Your item could not be added (${response.status}). Please try again.`;
            showError(description + (response.status >= 500
              ? ' The result may be uncertain. Check your cart before trying again.' : ''));
            return;
          }
          cartSuccess(result || {}, quantity);
          void updateCartCount();
        } catch (_) {
          showError(acknowledged
            ? 'Your item was added to the cart. Open your cart to review your items.'
            : 'The connection was interrupted and the result is uncertain. Check your cart before trying again to avoid adding the same item twice.');
          if (acknowledged) void updateCartCount();
        } finally {
          window.clearTimeout(timeout);
          submitting = false;
          updateButtons();
        }
      });
      submitButtons.forEach((button) => {
        const nativeSubmit = button.form === purchase && button.type === 'submit';
        if (nativeSubmit) return;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          if (button.disabled || submitting) return;
          if (typeof purchase.requestSubmit === 'function') purchase.requestSubmit();
          else purchase.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
      });
    } else {
      updateButtons();
    }

    all('button[data-pc-close]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      const dialog = button.closest('dialog');
      if (dialog && typeof dialog.close === 'function') dialog.close();
    }));
    all('dialog').forEach((dialog) => dialog.addEventListener('click', (event) => {
      if (event.target === dialog && typeof dialog.close === 'function') dialog.close();
    }));

    const menu = one('#pc-menu');
    const menuButtons = all('button[data-pc-menu]');
    const setMenu = (open) => {
      if (!menu) return;
      menu.hidden = !open;
      menuButtons.forEach((button) => button.setAttribute('aria-expanded', String(open)));
    };
    if (menu) {
      setMenu(!menu.hidden);
      menuButtons.forEach((button) => {
        button.setAttribute('aria-controls', 'pc-menu');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          setMenu(menu.hidden);
        });
      });
      all('a[href]', menu).forEach((link) => link.addEventListener('click', () => setMenu(false)));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !menu.hidden) setMenu(false);
      });
    }

    const comparisonRange = one('input#pc-comparison-range');
    if (comparisonRange) {
      const comparison = comparisonRange.closest('.pc-comparison') || one('.pc-comparison');
      const updateComparison = () => {
        if (!comparison) return;
        const value = Number(comparisonRange.value);
        if (Number.isFinite(value)) comparison.style.setProperty('--split', `${Math.max(0, Math.min(100, value))}%`);
      };
      comparisonRange.addEventListener('input', updateComparison);
      comparisonRange.addEventListener('change', updateComparison);
      updateComparison();
    }

    const videoObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (!entry.isIntersecting) entry.target.pause(); });
    }, { threshold: 0 }) : null;
    const videos = all('.pc-video-card video');
    videos.forEach((video) => {
      video.preload = 'metadata';
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.autoplay = false;
      video.removeAttribute('autoplay');
      const card = video.closest('.pc-video-card');
      const buttons = card ? all('button[data-pc-video]', card) : [];
      let playVersion = 0;
      const updateVideoButtons = () => buttons.forEach((button) => {
        const playing = !video.paused && !video.ended;
        button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        button.setAttribute('aria-pressed', String(playing));
        button.classList.toggle('is-playing', playing);
      });
      buttons.forEach((button) => button.addEventListener('click', async (event) => {
        event.preventDefault();
        if (!video.paused) {
          playVersion += 1;
          video.pause();
          updateVideoButtons();
          return;
        }
        const version = ++playVersion;
        videos.forEach((other) => { if (other !== video) other.pause(); });
        try {
          await video.play();
          if (version !== playVersion) video.pause();
        } catch (_) {
          video.pause();
        }
        updateVideoButtons();
      }));
      ['play', 'playing', 'pause', 'ended', 'error', 'emptied'].forEach((name) =>
        video.addEventListener(name, updateVideoButtons));
      video.addEventListener('pause', () => { playVersion += 1; });
      updateVideoButtons();
      if (videoObserver) videoObserver.observe(video);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach((video) => video.pause());
    });
    all('[data-pc-video-step]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      const direction = Number(button.getAttribute('data-pc-video-step'));
      if (direction !== -1 && direction !== 1) return;
      let container = button.parentElement;
      let track = null;
      while (container && page.contains(container) && !track) {
        track = one('.pc-video-track', container);
        container = container.parentElement;
      }
      if (!track) return;
      const card = one('.pc-video-card', track) || track.firstElementChild;
      if (!card) return;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      const margin = window.getComputedStyle(card);
      const distance = card.getBoundingClientRect().width + gap
        + (parseFloat(margin.marginLeft) || 0) + (parseFloat(margin.marginRight) || 0);
      track.scrollBy({ left: direction * distance, behavior: behavior() });
    }));

    all('[data-pc-back-top]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: behavior() });
    }));
    const sticky = one('.pc-sticky');
    if (sticky && purchase) {
      const updateSticky = () => {
        const rect = purchase.getBoundingClientRect();
        const visible = window.scrollY > 400 && rect.bottom <= 0;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      };
      const scheduledSticky = onFrame(updateSticky);
      if (typeof IntersectionObserver === 'function') {
        const stickyObserver = new IntersectionObserver(updateSticky, { threshold: 0 });
        stickyObserver.observe(purchase);
      }
      window.addEventListener('scroll', scheduledSticky, { passive: true });
      window.addEventListener('resize', scheduledSticky, { passive: true });
      updateSticky();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
